import { Component, Input, OnChanges, SimpleChanges, signal } from '@angular/core'
import { CommonModule } from '@angular/common'

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { DialogModule } from '@openng/optimus-ui/dialog'
import { NgxEchartsModule } from 'ngx-echarts'
import { FuseqTableComponent } from '../../../FuseQ/fuseq-modules/fuseqTable/fqtable.component'
import { AwpSpeedtestApiService } from '../../../_services'
import type { EChartsOption } from 'echarts'
import dayjs from 'dayjs'
import { FqDialogNavComponent } from '../../../shared/fq-dialog-nav.component'

const NETWORKS = [
  { key: 'cellular', label: 'Cellular' },
  { key: 'nrd1', label: 'NRD1' },
  { key: 'nrd2', label: 'NRD2' },
  { key: 'mpls1', label: 'MPLS1' },
  { key: 'mpls2', label: 'MPLS2' }
]

const COLORS = ['#0ea5e9', '#4f46e5', '#2b897a', '#f59e0b', '#ef4444']

import { OptimUiSharedModule } from '../../../shared/optimus-ui-shared.module';
@Component({
  standalone: true,
  selector: 'app-speedtest-history-table',
  templateUrl: './speedtest-history-table.component.html',
  styleUrls: ['./speedtest-history-table.component.scss'],
  imports: [OptimUiSharedModule, CommonModule, FontAwesomeModule, DialogModule, NgxEchartsModule, FuseqTableComponent, FqDialogNavComponent]
})
export class SpeedtestHistoryTableComponent implements OnChanges {
  @Input() frazionario: string | null = null

  value = signal<any[]>([])
  downloadTrendOptions = signal<EChartsOption>({})
  uploadTrendOptions = signal<EChartsOption>({})
  isDownloadCollapsed = true
  isUploadCollapsed = true
  private downloadChart?: any
  private uploadChart?: any

  historyDetailsVisible = false
  headerHistoryDetails = ''
  tunnel = ''
  traffic: any[] = []

  detailSections = [
    { key: 'tunnel', label: 'Tunnel Status', icon: ['fas', 'circle'] },
    { key: 'traffic', label: 'Traffic', icon: ['fas', 'circle'] }
  ]
  detailTab = 'tunnel'

  constructor (private readonly awpSpeedtestApiService: AwpSpeedtestApiService) {}

  ngOnChanges (changes: SimpleChanges) {
    if (changes['frazionario']) {
      this.loadHistory()
    }
  }

  loadHistory () {
    if (!this.frazionario) {
      this.value.set([])
      this.downloadTrendOptions.set({})
      this.uploadTrendOptions.set({})
      return
    }
    this.value.set([])
    this.awpSpeedtestApiService.Get_SpeedTestHistory(this.frazionario).subscribe(data => {
      this.value.set(data.map(item => ({
        ...item,
        cellular: this.buildSpeedHtml(item, 'cellular'),
        nrd1: this.buildSpeedHtml(item, 'nrd1'),
        nrd2: this.buildSpeedHtml(item, 'nrd2'),
        mpls1: this.buildSpeedHtml(item, 'mpls1'),
        mpls2: this.buildSpeedHtml(item, 'mpls2')
      })))
      this.buildTrends()
    }, error => {
      console.log(error)
    })
  }

  private buildTrends () {
    const ordered = [...this.value()].sort((a, b) =>
      dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
    )
    const dates = ordered.map(item => dayjs(item.start_date).format('YYYY-MM-DD HH:mm'))

    this.downloadTrendOptions.set(this.buildTrendOptions('download', dates, ordered, 'Download (Mbps)'))
    this.uploadTrendOptions.set(this.buildTrendOptions('upload', dates, ordered, 'Upload (Mbps)'))
  }

  private buildTrendOptions (metric: 'download' | 'upload', dates: string[], rows: any[], yName: string): EChartsOption {
    const series = NETWORKS.map((net, idx) => ({
      name: net.label,
      type: 'line' as const,
      smooth: true,
      showSymbol: true,
      itemStyle: { color: COLORS[idx] },
      lineStyle: { color: COLORS[idx] },
      data: rows.map(r => r[`${net.key}_${metric}_mbps`] ?? null)
    }))

    return {
      tooltip: { trigger: 'axis' },
      legend: { top: 'bottom' },
      grid: { left: '3%', right: '4%', bottom: '12%', containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLabel: { rotate: 30 }
      },
      yAxis: {
        type: 'value',
        name: yName
      },
      series
    }
  }

  buildSpeedHtml (row: any, prefix: string): string {
    const dl = row[`${prefix}_download_mbps`]
    const ul = row[`${prefix}_upload_mbps`]
    let html = `${dl ?? ''}`
    if (dl) html += ' <i class="fa-regular fa-circle-down icon-down"></i><span> - </span>'
    html += `${ul ?? ''}`
    if (ul) html += ' <i class="fa-regular fa-circle-up icon-up"></i>'
    return html
  }

  onDetails (row: any) {
    this.headerHistoryDetails = 'Details Frazionario: ' + row.frazionario
    this.tunnel = row.tunnel_status?.htmlResult
    this.traffic = row.traffic
    this.historyDetailsVisible = true
  }

  closeHistoryDetails () {
    this.historyDetailsVisible = false
    this.tunnel = ''
    this.traffic = []
  }

  convertBytes (bytes: number): string {
    if (!bytes) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  onDownloadChartInit (chart: any) {
    this.downloadChart = chart
  }

  onUploadChartInit (chart: any) {
    this.uploadChart = chart
  }

  toggleDownload () {
    this.isDownloadCollapsed = !this.isDownloadCollapsed
    if (!this.isDownloadCollapsed) {
      setTimeout(() => this.downloadChart?.resize(), 100)
    }
  }

  toggleUpload () {
    this.isUploadCollapsed = !this.isUploadCollapsed
    if (!this.isUploadCollapsed) {
      setTimeout(() => this.uploadChart?.resize(), 100)
    }
  }
}
